import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import dayjs from 'dayjs';
import { lastValueFrom } from 'rxjs';
import { LoadingService } from './loading.service';
import {
  BandmanagerRestApiAuthenticationService,
  BandmanagerRestApiContentManagementSettingsService,
  BandmanagerRestApiMyBandService,
  BandmanagerRestApiMyEventsService,
  BandmanagerRestApiMyUniformService,
} from '@digitale-menschen/bandmanager-rest-api';
import { UserStateActions } from '../../shared/user-state/user-state.actions';
import { BandStateActions } from '../../shared/band-state/band-state.actions';
import { EventsStateActions } from '../../shared/events-state/events-state.actions';
import { BandroomService } from './bandroom.service';
import { Store } from '@ngxs/store';
import { SettingsStateActions } from '../../shared/settings-state/settings-state.actions';
import { SettingsState } from '../../shared/settings-state/settings-state.state';
import { environment } from '../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from "@angular/router";
import { SessionService } from "./session.service";
import { AppStateActions } from "../../shared/app-state/app-state.actions";

@Injectable({
  providedIn: 'root',
})
export class InitializationService implements OnDestroy {
  // 30 min
  private dynBackgroundTimerInterval = 1800000;
  private dynBackgroundTimerRef: any = null;
  private dynBackgroundCssDay = 'var(--color-dyn-background-day)';
  private dynBackgroundCssNight = 'var(--color-dyn-background-night)';
  private dynBackgroundCssDefault = '--color-dyn-background';
  private dayStartHour = 7;
  private nightStartHour = 20;
  private destroyRef?: DestroyRef;
  private router = inject(Router);
  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private sessionService = inject(SessionService);
  private bandroomService = inject(BandroomService);
  private apiAuthenticationService = inject(BandmanagerRestApiAuthenticationService);
  private apiMyBandService = inject(BandmanagerRestApiMyBandService);
  private apiMyUniformService = inject(BandmanagerRestApiMyUniformService);
  private apiMyEventsService = inject(BandmanagerRestApiMyEventsService);
  private apiSettingsService = inject(BandmanagerRestApiContentManagementSettingsService);

  constructor() {
    this.destroyRef = inject(DestroyRef);
  }

  public async loadPublicSettings(): Promise<void> {
    this.store.dispatch(
      new SettingsStateActions.SetSettings(
        await lastValueFrom(this.apiSettingsService.contentManagementSettingsControllerGetSettings()),
      ),
    );
  }

  public startDynamicBackground(): void {
    this.store.select(SettingsState.settingByKey(environment.settingsKeys.dynamicBgDayStart)).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dayHours) => {
        this.dayStartHour = dayHours.hour;
        this.startDynamicBackgroundTimer();
      });

    this.store.select(SettingsState.settingByKey(environment.settingsKeys.dynamicBgNightStart)).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nightHours) => {
        this.nightStartHour = nightHours.hour;
        this.startDynamicBackgroundTimer();
      });
  }

  private startDynamicBackgroundTimer(): void {
    this.updateDynamicBackground();

    if (!this.dynBackgroundTimerRef) {
      this.dynBackgroundTimerRef = setInterval(() => {
        this.updateDynamicBackground();
      }, this.dynBackgroundTimerInterval);
    }
  }

  ngOnDestroy(): void {
    if (this.dynBackgroundTimerRef) {
      clearInterval(this.dynBackgroundTimerRef);
      this.dynBackgroundTimerRef = null;
    }
  }

  /**
   * Load data from Api and save it in State
   * @private
   */
  public async loadAndCacheUserData() {
    const success = await this.sessionService.requestForLoginCheck();
    if (!success) {
      this.loadingService.hide();
      throw new Error('User not logged in.');
    }
    this.store.dispatch(
      new UserStateActions.SetProfile(
        await lastValueFrom(this.apiAuthenticationService.authenticationControllerProfile())),
    );
    const myBand = await lastValueFrom(this.apiMyBandService.bandControllerGetMyBand());
    if (!myBand || !myBand.id) {
      // user has no band, user has been registered by GoogleSignin, without doing the onboarding
      // we redirect to onboarding
      this.loadingService.hide();
      this.store.dispatch(new AppStateActions.setIsOnboardingInProgress(true));
      await this.router.navigate(['/onboarding']);
      return;
    }
    this.store.dispatch(
      new BandStateActions.SetBand(myBand),
    );
    try {
      this.store.dispatch(
        new BandStateActions.SetRehearsal(
          await lastValueFrom(this.apiMyBandService.bandControllerGetMyLatestRehearsal()),
        ),
      );
    } catch (noRehearsal) {
      // Nothing to do, just catching if it there has not been a rehearsal yet.
    }
    this.store.dispatch(
      new BandStateActions.SetBankAccount(
        await lastValueFrom(this.apiMyBandService.bankaccountControllerGetMyBankaccount()),
      ),
    );
    this.store.dispatch(
      new BandStateActions.SetStats(
        await lastValueFrom(this.apiMyBandService.bandControllerGetMyBandStats()),
      ),
    );
    this.store.dispatch(
      new BandStateActions.SetUniform(
        await lastValueFrom(this.apiMyUniformService.uniformControllerGetMyUniform()),
      ),
    );
    this.store.dispatch(
      new EventsStateActions.setEventsParticipations(
        await lastValueFrom(this.apiMyEventsService.eventParticipationsControllerGetMyEventParticipations()),
      ),
    );
    this.store.dispatch(
      new SettingsStateActions.SetSettings(
        await lastValueFrom(this.apiSettingsService.contentManagementSettingsControllerGetSettings()),
      ),
    );
    await this.bandroomService.loadUpgrades(false);
    this.loadingService.hide();
  }

  private updateDynamicBackground(): void {
    const isNight = dayjs().isAfter(this.nightStartHour, 'hour') && dayjs().isBefore(this.dayStartHour, 'hour');
    document.documentElement.style.setProperty(this.dynBackgroundCssDefault,
      isNight ? this.dynBackgroundCssNight : this.dynBackgroundCssDay);
  }

  /**
   * check if target url is VerifyEmail / ResetPassword
   */
  public isTargetUrlSpecialPages(): boolean {
    const targetUrl = this.getTargetUrl();
    return targetUrl && targetUrl.indexOf('auth/verify') > -1 || targetUrl.indexOf('auth/password-reset') > -1;
  }

  private getTargetUrl(): string {
    let targetUrl = '';
    const currentNavigation = this.router.getCurrentNavigation();
    if (currentNavigation?.finalUrl?.root?.children?.primary?.segments) {
      targetUrl = currentNavigation.finalUrl.root.children.primary.segments.join('/');
    }
    return targetUrl;
  }
}
